import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from 'redux';
import { addError } from '@/store/slices/errorSlice';

/**
 * RTK Query error handling middleware
 * Automatically captures API errors and adds them to the global error state
 */
export const apiErrorMiddleware: Middleware = (store) => (next) => (action) => {
  // Handle RTK Query errors
  if (isRejectedWithValue(action)) {
    const error = action.payload as any;
    let errorMessage = 'An unexpected error occurred';

    // Prefer our structured RequestError: its `message` is already a single-line
    // summary with endpoint/url/op/status and its `details.kind` drives UX choices.
    if (error?.details?.kind) {
      // Session-expired is handled by SessionExpiredDialog — don't show a snackbar too
      if (error.details.kind === 'session-expired') {
        return next(action);
      }
      errorMessage = error.message ?? errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors)) {
      errorMessage = error.data.errors.map((e: any) => e.message).join(', ');
    } else if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.error) {
      errorMessage = error.error;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Don't add to global errors if it's a handled mutation error
    const isMutationError = (action.meta?.arg as any)?.endpointName?.includes('Mutation');
    if (!isMutationError) {
      store.dispatch(
        addError({
          message: errorMessage,
          type: 'error',
          details: error,
        })
      );
    }
  }

  return next(action);
};

/**
 * Helper to check if an RTK Query result has errors
 */
export const hasApiErrors = (result: any): boolean => {
  return result?.errors && Array.isArray(result.errors) && result.errors.length > 0;
};

/**
 * Extract error messages from API response
 */
export const getApiErrorMessages = (result: any): string[] => {
  if (!result?.errors || !Array.isArray(result.errors)) {
    return [];
  }
  
  return result.errors.map((error: any) => {
    if (error.field) {
      return `${error.field}: ${error.message}`;
    }
    return error.message;
  });
};